import {useObserver} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {PRIMARY_BACKGROUND_COLOR} from "../../Config/Colors";
import Logger from "../../Domain/Logger/Logger";
import PowerClientService from "../../Domain/Services/PowerClientService";
import PowerActionService from "../../Domain/Services/PowerActionService";
import PowerClient from "../../Domain/Models/PowerClient";
import {powerClientRepository} from "../../Domain/Repositories/PowerClientRepository";
import DataGridItem from "../Shared/DataGridItem";
import ListHeader from "../Shared/ListHeader";
import ListFooter from "../Shared/ListFooter";
import ScreenTitle from "../Shared/ScreenTitle";
import EditPowerClientModal from "../Shared/Modals/EditPowerClientModal";
import {
    CONNECTION_STATUS_TIMINGS_MILLISECONDS,
    POWER_CLIENT_USE_DUMMY_DATA,
} from "../../Config/Parameters";
import useRefreshConnectionStatus from "../../Domain/Hooks/useRefreshConnectionStatus";
import PowerActionModal from "../Shared/Modals/PowerActionModal";
import {userRepository} from "../../Domain/Repositories/UserRepository";
import GenericNotificationModal from "../Shared/Modals/GenericNotificationModal";
import ConfirmationModal from "../Shared/Modals/ConfirmationModal";
import { FETCHING_FAILED_TITLE, FETCHING_SUCCESS_TITLE, IS_NOT_LOGGED_IN, POWER_CLIENTS_DASHBOARD_TITLE, PRIMARY_TITLE } from '../../Config/Translations';

const logger = Logger.Create('PowerClientSubView');

interface PowerClientSubViewProps
{
    /**
     * Callback when the menu was pressed.
     */
    onMenuButtonPressed?: () => void;

    /**
     * Callback when the login was pressed.
     */
    onLoginPressed?: () => void;
}

let timer: NodeJS.Timeout | undefined;

/**
 * Renders the Power Client View.
 */
export default function PowerClientSubView({
    onMenuButtonPressed,
    onLoginPressed,
}: PowerClientSubViewProps): JSX.Element
{
    const [showActionModal, setShowActionModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState<PowerClient>(powerClientRepository.dummyPowerClient);
    const [showIndicator, setShowIndicator] = useState(false);
    const [indicatorTitle, setIndicatorTitle] = useState(FETCHING_SUCCESS_TITLE);
    const [actionModalMessage, setActionModalMessage] = useState("");
    const [showNotification, setShowNotification] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [action, setAction] = useState("");
    
    const { refreshConnection, stopRefresh } = useRefreshConnectionStatus(async () =>
    {
       await refreshList();
    });
    
    const refreshList = async (): Promise<void> =>
    {
        setIndicatorTitle(FETCHING_SUCCESS_TITLE);

        // Timer interval elapsed. Check for all power clients.
        const success = await PowerClientService.getClients(POWER_CLIENT_USE_DUMMY_DATA);

        if (!success)
        {
            setIndicatorTitle(FETCHING_FAILED_TITLE);
        }

        updateIndicator();
    }

    const updateIndicator = (): void =>
    {
        if (timer !== undefined)
        {
            clearTimeout(timer);
            timer = undefined;
        }

        setShowIndicator(true);
        timer = setTimeout(() =>
        {
            setShowIndicator(false);
        }, CONNECTION_STATUS_TIMINGS_MILLISECONDS.indicatorDuration);
    }
    
    useEffect(() =>
    {
        let isMounted = true;
        
        if (isMounted)
        {
            refreshConnection();
        }

        return () => {
            isMounted = false
            stopRefresh();
        };
    }, []);

    const onWakeButtonSelected = async (): Promise<void> =>
    {
        if (!userRepository.isLoggedIn())
        {
            setShowNotification(true);
            return;
        }
        
        setAction("wake");
        setShowConfirmation(true);
    }
    
    const wake = async (): Promise<void> =>
    {
        const result = await PowerActionService.wake();
        setActionModalMessage(result.message);
        setShowActionModal(true);
    }

    const onShutdownButtonSelected = async (): Promise<void> =>
    {
        if (!userRepository.isLoggedIn())
        {
            setShowNotification(true);
            return;
        }
        
        setAction("shutdown");
        setShowConfirmation(true);
    }
    
    const shutdown = async (): Promise<void> =>
    {
        const result = await PowerActionService.shutdown();
        setActionModalMessage(result.message);
        setShowActionModal(true);
    }

    const onEditButtonPressed = (powerClient: PowerClient): void =>
    {
        if (!userRepository.isLoggedIn())
        {
            setShowNotification(true);
            return;
        }
        
        setSelectedClient(powerClient);
        setShowEditModal(true);
    }

    const onCloseModalPressed = (shouldUpdate: boolean): void =>
    {
        logger.info(`Close modal was selected. Should make another request?: ${shouldUpdate}`);

        if (shouldUpdate)
        {
            refreshList();
        }

        setShowEditModal(false);
    }

    const render = (powerClients: PowerClient[]): JSX.Element =>
    {
        return (
            <FlatList
                bounces
                horizontal={false}
                data={powerClients}
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                renderItem={({ item: entry }): JSX.Element => (
                    <DataGridItem
                        powerClient={entry}
                        isSelected={powerClientRepository.isChecked(entry.id)}
                        onSelected={onDataItemChecked}
                        onEditButtonSelected={onEditButtonPressed}
                    />
                )}
                style={styles.flatList}
                keyExtractor={(entry, index): string => `${entry.id}-${index}`}
            />
        )
    }

    const onDataItemChecked = (enabled: boolean, id: string) =>
    {
        if (enabled)
        {
            powerClientRepository.addSelectedClient(id);
            return;
        }
        
        powerClientRepository.removeSelectedClient(id);
    }

    const onHeaderCheckboxSelected = (state: boolean) =>
    {
        if (state)
        {
            powerClientRepository.addAllSelectedClients();
            return;
        }

        powerClientRepository.clearAllSelectedClients();
    }
    
    const onProceedSelected = (): void =>
    {
        setShowConfirmation(false);
        
        if (action.trim() === "")
        {
            logger.error("No state requested!");
            return;
        }
        
        if (action === "wake")
        {
            wake();
            return;
        }
        
        shutdown();
    }

    return useObserver(() => (
        <View style={styles.container}>
            <View style={{flex: 0.02}} />

            <ScreenTitle
                title={PRIMARY_TITLE}
                showLogin
                onMenuPressed={onMenuButtonPressed}
                underline
                onLoginPressed={onLoginPressed}
            />
            
            <ScreenTitle
                title={POWER_CLIENTS_DASHBOARD_TITLE}
                showMenu={false}
                showLoading={showIndicator}
                loadingTitle={indicatorTitle}
            />
            
            <ListHeader
                onCheckboxSelected={onHeaderCheckboxSelected}
            />

            {render(powerClientRepository.powerClients)}

            <ListFooter
                totalSelectedItems={powerClientRepository.currentlySelectedClients.length}
                totalItems={powerClientRepository.powerClients.length}
                onWakePressed={onWakeButtonSelected}
                onShutdownPressed={onShutdownButtonSelected}
                onRefreshPressed={refreshList}
            />

            <EditPowerClientModal
                show={showEditModal}
                selectedClient={selectedClient}
                onCloseButtonSelected={onCloseModalPressed}
            />
            
            <PowerActionModal
                show={showActionModal}
                onCloseButtonSelected={() => setShowActionModal(false)}
                message={actionModalMessage}
            />

            <GenericNotificationModal
                show={showNotification}
                onCloseButtonSelected={() => setShowNotification(false)}
                message={IS_NOT_LOGGED_IN}
            />
            
            <ConfirmationModal
                show={showConfirmation}
                onCancelSelected={() => setShowConfirmation(false)}
                onProceedSelected={onProceedSelected}
            />
        </View>
    ));
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
        width: "100%",
        height: "100%",
        alignItems: 'center',
        justifyContent: 'center',
        alignContent: 'center',
    },
    flatList: {
        flex: 0.9,
        width: "100%",
        backgroundColor: PRIMARY_BACKGROUND_COLOR,
    },
});
